import type { VenueHours } from '../model/schema';

/** A single day split into structured fields for editing. */
export interface WorkingHoursSlot {
  /** Weekday key ("mon"–"sun") — carried through, never edited. */
  label: string;
  /** Whether the venue is open this day. When false, `open`/`close` are ignored. */
  isOpen: boolean;
  /** Opening time as `HH:MM` (empty when unparseable). */
  open: string;
  /** Closing time as `HH:MM` (empty when unparseable). */
  close: string;
  orderFrom?: string | undefined;
  orderTo?: string | undefined;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Default hours seeded into a day when it is toggled from closed to open. */
export const DEFAULT_OPEN = '09:00';
export const DEFAULT_CLOSE = '18:00';

/** True for a well-formed 24-hour `HH:MM` string. */
export function isHhMm(value: string): boolean {
  return HHMM.test(value);
}

/**
 * Split a preformatted `"HH:MM - HH:MM"` range into `{ open, close }`. Missing
 * or malformed endpoints come back empty, so the editor can flag them. An empty
 * string means the day is closed.
 */
export function parseHoursRange(hours: string): { open: string; close: string } {
  const [rawOpen, rawClose] = hours.split(/\s*[-–—]\s*/);
  const open = (rawOpen ?? '').trim();
  const close = (rawClose ?? '').trim();
  return {
    open: isHhMm(open) ? open : '',
    close: isHhMm(close) || close === '24:00' ? close : '',
  };
}

/** Build the `"HH:MM - HH:MM"` range the backend/list rows store. */
export function formatHoursRange(open: string, close: string): string {
  return `${open} - ${close}`;
}

/**
 * Explode the weekly schedule into editable per-day slots. A day whose `hours`
 * don't parse to a valid range is treated as closed.
 */
export function toWorkingHoursSlots(workingHours: VenueHours[]): WorkingHoursSlot[] {
  return workingHours.map((day) => {
    const { open, close } = parseHoursRange(day.hours);
    const isOpen = open !== '' && close !== '';
    return {
      label: day.label,
      ...(day.orderFrom !== undefined ? { orderFrom: day.orderFrom } : {}),
      ...(day.orderTo !== undefined ? { orderTo: day.orderTo } : {}),
      isOpen,
      open: open || DEFAULT_OPEN,
      close: close || DEFAULT_CLOSE,
    };
  });
}

/**
 * Collapse editable slots back into the `{ label, hours }` schedule. A closed
 * day serializes to an empty `hours` string.
 */
export function fromWorkingHoursSlots(slots: WorkingHoursSlot[]): VenueHours[] {
  return slots.map((slot) => ({
    label: slot.label,
    ...(slot.orderFrom !== undefined
      ? { orderFrom: slot.isOpen ? slot.orderFrom : '' }
      : {}),
    ...(slot.orderTo !== undefined ? { orderTo: slot.isOpen ? slot.orderTo : '' } : {}),
    hours: slot.isOpen ? formatHoursRange(slot.open, slot.close) : '',
  }));
}

/**
 * The `HH:MM` that means "midnight at the END of the day" rather than the
 * start of it. A venue open `08:00 - 00:00` closes at midnight; read as a
 * same-day ordinal, `00:00` is 0 minutes and so sorts before every opening
 * time, which is what made the picker refuse to accept it.
 */
export const MIDNIGHT = '00:00';

/**
 * True when `close` is a valid end for a day that opens at `open`.
 *
 * Closing normally has to land strictly after opening, with one exception:
 * `00:00` as a CLOSING time is midnight at the end of the day, so it is the
 * latest possible close and is always valid. `00:00` as an OPENING time still
 * means the start of the day and is unaffected.
 */
export function isValidHoursRange(open: string, close: string): boolean {
  return close === MIDNIGHT || close > open;
}
